import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../utils/api';
import { Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';

const validators = {
  name: (v) => {
    if (!v.trim()) return 'Name is required';
    if (v.trim().length < 2) return 'Name must be at least 2 characters';
    if (!/^[a-zA-Z\s]+$/.test(v.trim())) return 'Name can only contain letters and spaces';
    return '';
  },
  email: (v) => {
    if (!v.trim()) return 'Email is required';
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v.trim())) return 'Please enter a valid email';
    return '';
  },
  phone: (v) => {
    if (!v.trim()) return 'Phone number is required';
    if (!/^[6-9]\d{9}$/.test(v.trim())) return 'Please enter a valid 10-digit Indian mobile number';
    return '';
  },
  password: (v) => {
    if (!v) return 'Password is required';
    if (v.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(v)) return 'Password must include an uppercase letter';
    if (!/[a-z]/.test(v)) return 'Password must include a lowercase letter';
    if (!/[0-9]/.test(v)) return 'Password must include a number';
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(v)) return 'Password must include a special character';
    return '';
  },
  confirmPassword: (v, password) => {
    if (!v) return 'Please confirm your password';
    if (v !== password) return 'Passwords do not match';
    return '';
  },
};

const Register = () => {
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('form'); // 'form' | 'otp'
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      const err = field === 'confirmPassword'
        ? validators.confirmPassword(value, form.password)
        : validators[field](value);
      setErrors(prev => ({ ...prev, [field]: err }));
    }
  };

  const validateAll = () => {
    const errs = {
      name: validators.name(form.name),
      email: validators.email(form.email),
      phone: validators.phone(form.phone),
      password: validators.password(form.password),
      confirmPassword: validators.confirmPassword(form.confirmPassword, form.password),
    };
    setErrors(errs);
    return !Object.values(errs).some(Boolean);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAll()) return;
    setLoading(true);
    try {
      await api.post('/auth/register', { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), password: form.password });
      toast.success('OTP sent to your email!');
      setStep('otp');
      startResendCooldown();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const startResendCooldown = () => {
    setResendCooldown(30);
    const timer = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) { setOtpError('Enter a valid 6-digit OTP'); return; }
    setOtpError('');
    setLoading(true);
    try {
      const response = await api.post('/auth/register/verify', { email: form.email.trim(), code: otp });
      loginWithToken(response.data.token, response.data.user);
      toast.success('Account verified! Welcome to Vastram!');
      navigate('/');
    } catch (error) {
      setOtpError(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    try {
      await api.post('/auth/register/resend-otp', { email: form.email.trim() });
      toast.success('New OTP sent!');
      startResendCooldown();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend');
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 bg-transparent border text-sm text-forest focus:outline-none transition-colors ${
      errors[field] ? 'border-red-400 focus:border-red-500' : 'border-forest/20 focus:border-gold'
    }`;

  if (step === 'otp') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ivory px-4 py-12" data-testid="register-otp-page">
        <div className="w-full max-w-md">
          <button onClick={() => setStep('form')} className="flex items-center text-forest/60 hover:text-forest text-sm mb-6 transition-colors" data-testid="back-to-form-button">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </button>
          <h1 className="font-heading text-3xl text-forest mb-2 text-center">Verify Your Email</h1>
          <p className="text-forest/60 text-sm mb-8 text-center">We've sent a 6-digit OTP to <strong>{form.email}</strong></p>
          <form onSubmit={handleVerifyOtp} className="space-y-5">
            <div>
              <input type="text" value={otp} onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setOtpError(''); }}
                placeholder="Enter 6-digit OTP" maxLength={6}
                className={`w-full px-4 py-3 bg-transparent border text-center text-2xl tracking-[0.5em] font-mono text-forest focus:outline-none transition-colors ${otpError ? 'border-red-400' : 'border-forest/20 focus:border-gold'}`}
                data-testid="register-otp-input" autoFocus />
              {otpError && <p className="text-red-500 text-xs mt-1" data-testid="otp-error">{otpError}</p>}
            </div>
            <button type="submit" disabled={loading} className="w-full bg-forest text-ivory py-3 text-sm uppercase tracking-[0.15em] hover:bg-forest/90 transition-colors disabled:opacity-50 flex items-center justify-center" data-testid="verify-otp-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & Create Account'}
            </button>
            <p className="text-center text-sm text-forest/60">
              Didn't receive?{' '}
              <button type="button" onClick={handleResendOtp} disabled={resendCooldown > 0}
                className={`${resendCooldown > 0 ? 'text-forest/30' : 'text-gold hover:underline'} transition-colors`} data-testid="resend-otp-button">
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
              </button>
            </p>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-ivory px-4 py-12" data-testid="register-page">
      <div className="w-full max-w-md">
        <h1 className="font-heading text-3xl text-forest mb-2 text-center">Create Account</h1>
        <p className="text-forest/60 text-sm mb-8 text-center">Join the Vastram family</p>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
          <div>
            <input type="text" placeholder="Full Name" value={form.name} onChange={(e) => handleChange('name', e.target.value)}
              className={inputClass('name')} data-testid="register-name-input" />
            {errors.name && <p className="text-red-500 text-xs mt-1" data-testid="name-error">{errors.name}</p>}
          </div>
          <div>
            <input type="email" placeholder="Email Address" value={form.email} onChange={(e) => handleChange('email', e.target.value)}
              className={inputClass('email')} data-testid="register-email-input" />
            {errors.email && <p className="text-red-500 text-xs mt-1" data-testid="email-error">{errors.email}</p>}
          </div>
          <div>
            <div className="flex">
              <span className="px-3 py-3 border border-r-0 border-forest/20 text-forest/60 text-sm bg-forest/5">+91</span>
              <input type="tel" placeholder="Phone Number" value={form.phone} onChange={(e) => handleChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                className={`flex-1 px-4 py-3 bg-transparent border text-sm text-forest focus:outline-none transition-colors ${errors.phone ? 'border-red-400 focus:border-red-500' : 'border-forest/20 focus:border-gold'}`}
                data-testid="register-phone-input" />
            </div>
            {errors.phone && <p className="text-red-500 text-xs mt-1" data-testid="phone-error">{errors.phone}</p>}
          </div>
          <div>
            <div className="relative">
              <input type={showPassword ? 'text' : 'password'} placeholder="Password" value={form.password} onChange={(e) => handleChange('password', e.target.value)}
                className={inputClass('password')} data-testid="register-password-input" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-forest/40 hover:text-forest" tabIndex={-1}>
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.password && <p className="text-red-500 text-xs mt-1" data-testid="password-error">{errors.password}</p>}
          </div>
          <div>
            <div className="relative">
              <input type={showConfirm ? 'text' : 'password'} placeholder="Confirm Password" value={form.confirmPassword} onChange={(e) => handleChange('confirmPassword', e.target.value)}
                className={inputClass('confirmPassword')} data-testid="register-confirm-password-input" />
              <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-forest/40 hover:text-forest" tabIndex={-1}>
                {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.confirmPassword && <p className="text-red-500 text-xs mt-1" data-testid="confirm-password-error">{errors.confirmPassword}</p>}
          </div>
          <button type="submit" disabled={loading} className="w-full bg-forest text-ivory py-3 text-sm uppercase tracking-[0.15em] hover:bg-forest/90 transition-colors disabled:opacity-50 flex items-center justify-center" data-testid="register-submit-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign Up'}
          </button>
        </form>
        <p className="text-center text-sm text-forest/60 mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-gold hover:underline" data-testid="login-link">Sign In</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
